import type { ApolloError } from '@apollo/client';
import { useMutation } from '@apollo/client';
import type * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ARIA_LABELS } from '../../constants/ariaLabels.constants';
import { USER } from '../../constants/user.constants';
import { SET_PROFESSIONAL_CONTEXT_MUTATION } from '../../graphql/resolvers/userMutations/setProfessionalContext.gql';
import { UPDATE_USER_PREFERENCES_MUTATION } from '../../graphql/resolvers/userMutations/updateUserPreferences.gql';
import type { TabConfig } from '../../interfaces/globals.interface';
import type {
  ProfessionalContextInput,
  UserSliceState,
} from '../../interfaces/user.interface';
import { userSlice } from '../../store/user.store';
import SimpleTabs from '../UI/SimpleTabs';
import { BiographyView } from './BiographyView';
import { ProfessionalBackgroundView } from './ProfessionalBackgroundView';
import { ProfileView } from './ProfileView';
import './user-profile.scss';

interface UserState {
  userStore: UserSliceState;
}

interface UserProfileProps {
  username: string;
}

/**
 * Renders the user profile page (3 vertical-rail tabs):
 * Profile, Bio, Professional Background. Hosts the Plan C
 * setProfessionalContext + updateUserPreferences wiring.
 */
const UserProfile = ({ username }: UserProfileProps): React.ReactElement => {
  const dispatch = useDispatch();
  const location = useLocation();
  const hashSlug = location.hash.replace(/^#/, '') || undefined;
  const userStore = useSelector((state: UserState) => state.userStore.user);
  const userAccountId = userStore?.id;
  const { UPDATE_USER } = userSlice.actions;

  const hasContext = Boolean(userStore?.userProfile?.professionalContext);
  const optedOut = Boolean(userStore?.userPreferences?.planCOptedOut);
  const showPending = !hasContext && !optedOut;

  const [setProfessionalContext] = useMutation(
    SET_PROFESSIONAL_CONTEXT_MUTATION,
    {
      onCompleted: ({ setProfessionalContext: updated }) => {
        if (updated) {
          dispatch(
            // @ts-ignore — userStore is loosely typed by GET_USER_SUCCESS
            UPDATE_USER({
              ...userStore,
              userProfile: {
                ...(userStore?.userProfile ?? { isPublicPerson: false }),
                professionalContext: updated,
              },
            }),
          );
        }
      },
      onError: (ex: ApolloError) => {
        console.error('Error saving professional context:', ex);
      },
    },
  );

  const [updateUserPreferences] = useMutation(
    UPDATE_USER_PREFERENCES_MUTATION,
    {
      onCompleted: ({ updateUserPreferences: updated }) => {
        if (updated) {
          dispatch(
            // @ts-ignore — userStore is loosely typed by GET_USER_SUCCESS
            UPDATE_USER({
              ...userStore,
              userPreferences: {
                ...(userStore?.userPreferences ?? {}),
                ...updated,
              },
            }),
          );
        }
      },
      onError: (ex: ApolloError) => {
        console.error('Error updating user preferences:', ex);
      },
    },
  );

  const handleSubmitContext = async (
    input: ProfessionalContextInput,
  ): Promise<void> => {
    await setProfessionalContext({ variables: { input } });
  };

  const handleOptOut = async (): Promise<void> => {
    await updateUserPreferences({
      variables: { input: { userId: userAccountId, planCOptedOut: true } },
    });
  };

  const handleReEnable = async (): Promise<void> => {
    await updateUserPreferences({
      variables: { input: { userId: userAccountId, planCOptedOut: false } },
    });
  };

  const tabs: TabConfig[] = [
    {
      label: USER.TAB_LABELS.PROFILE,
      child: <ProfileView username={username} />,
      slug: 'profile',
    },
    {
      label: USER.TAB_LABELS.BIO,
      child: <BiographyView username={username} />,
      slug: 'bio',
    },
    {
      label: (
        <>
          {USER.TAB_LABELS.PROFESSIONAL_BACKGROUND}
          {showPending && (
            <span
              className="user-profile__pending"
              role="img"
              aria-label="Section needs attention"
              data-testid="professional-background-pending"
            >
              {' '}
              &bull;
            </span>
          )}
        </>
      ),
      child: (
        <ProfessionalBackgroundView
          context={userStore?.userProfile?.professionalContext ?? null}
          optedOut={optedOut}
          onSubmit={handleSubmitContext}
          onOptOut={handleOptOut}
          onReEnable={handleReEnable}
        />
      ),
      slug: 'professional-background',
    },
  ];

  return (
    <section className="user-profile">
      <header className="user-profile__header">
        <h1>Profile</h1>
      </header>
      <SimpleTabs
        ariaLabel={ARIA_LABELS.USER_SETTINGS}
        tabs={tabs}
        tabsGroupName="user-profile"
        parentComponent="user-profile"
        defaultSlug={hashSlug}
        orientation="horizontal"
      />
    </section>
  );
};

export default UserProfile;
